import { type ChangeEvent, Fragment, type KeyboardEvent, type ReactNode, type TextareaHTMLAttributes, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { UserDTO } from "@justone/shared";
import { useUsers } from "../api/queries";
import { matchScore } from "../lib/match";
import { type MentionTag, replaceRange, shiftTags, tagLabel, type TaggedText, toBody } from "../lib/mentionTags";
import { Avatar } from "./ui";

/** 커서 앞의 `@검색어` */
const MENTION_AT_CARET = /(^|\s)@([\p{L}\p{N}_.-]*)$/u;

const isComposingKey = (e: KeyboardEvent) => e.nativeEvent.isComposing || e.keyCode === 229;

/** 뒷판이 입력창에서 그대로 가져오는 모양 */
const COPIED = [
  "paddingTop",
  "paddingLeft",
  "paddingBottom",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "textIndent",
] as const;

interface MentionState {
  query: string;
  start: number;
  index: number;
}

/**
 * @멘션을 고르는 입력창 (메시지 쓰기·고치기).
 * 고른 사람은 "@황승현 (kernel)" 태그로 보이고, 태그 바로 뒤에서 백스페이스를 누르면 고르기 전에 치던 "@황승"으로 돌아간다.
 * 보낼 글(body)에서는 "@kernel"이 된다.
 */
export function useMentionField(initial: TaggedText, onChange?: (v: TaggedText) => void) {
  const [value, setValue] = useState(initial);
  const [mention, setMention] = useState<MentionState | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  const backdrop = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const { data: users = [] } = useUsers();

  // 바꾼 뒤의 커서 위치. 그린 직후에 바로 놓는다 (다음 프레임까지 미루면 그사이 친 글자가 엉뚱한 곳에 들어간다)
  const pendingCaret = useRef<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
    if (pendingCaret.current !== null) {
      el.setSelectionRange(pendingCaret.current, pendingCaret.current);
      pendingCaret.current = null;
    }
    // 뒤에 깔린 태그 색이 글자와 정확히 겹치도록 입력창의 글꼴·여백·테두리·스크롤바를 그대로 따른다
    const b = backdrop.current;
    if (b) {
      const cs = getComputedStyle(el);
      for (const k of COPIED) b.style[k] = cs[k];
      const scrollbar = el.offsetWidth - el.clientWidth - parseFloat(cs.borderLeftWidth) - parseFloat(cs.borderRightWidth);
      b.style.paddingRight = `${parseFloat(cs.paddingRight) + Math.max(0, scrollbar)}px`;
      b.scrollTop = el.scrollTop;
    }
  }, [value]);

  const detect = (text: string, caret: number) => {
    const m = MENTION_AT_CARET.exec(text.slice(0, caret));
    setMention(m ? { query: m[2]!, start: caret - m[2]!.length - 1, index: 0 } : null);
  };

  const apply = (next: TaggedText, caret?: number) => {
    valueRef.current = next;
    setValue(next);
    onChangeRef.current?.(next);
    if (caret !== undefined) {
      pendingCaret.current = caret;
      detect(next.text, caret);
    }
  };

  const onTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const el = e.target;
    const caret = el.selectionStart ?? el.value.length;
    const cur = valueRef.current;
    apply({ text: el.value, tags: shiftTags(cur.tags, cur.text, el.value, caret) });
    detect(el.value, caret);
  };

  const candidates = useMemo(() => {
    if (!mention) return [];
    const q = mention.query.toLowerCase();
    // 아이디 앞부분, 이름 일부, 이름 초성("ㅎㅅㅎ" → 황승현) 모두. 잘 맞는 순서로
    return users
      .filter((u) => u.active && u.username)
      .map((u) => ({ u, score: Math.max(u.username!.startsWith(q) ? 3 : 0, matchScore(u.displayName, q)) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map((x) => x.u);
  }, [mention, users]);

  const pick = (u: UserDTO) => {
    if (!mention || !u.username) return;
    const el = ref.current!;
    const cur = valueRef.current;
    const caret = el.selectionStart ?? cur.text.length;
    const label = tagLabel({ displayName: u.displayName, username: u.username });
    const next = replaceRange(cur, mention.start, caret, `${label} `);
    const tag: MentionTag = { start: mention.start, end: mention.start + label.length, username: u.username, query: cur.text.slice(mention.start, caret) };
    setMention(null);
    apply({ text: next.text, tags: [...next.tags, tag].sort((a, b) => a.start - b.start) });
    pendingCaret.current = tag.end + 1;
    el.focus();
  };

  // 태그 지우기: 뒤에서 백스페이스 → 고르기 전에 치던 글로, 앞에서 Delete → 태그를 통째로.
  // 키보드마다 keydown이 다르게 오므로(휴대폰 키보드) beforeinput으로 받는다
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onBefore = (e: InputEvent) => {
      const back = e.inputType === "deleteContentBackward";
      if ((!back && e.inputType !== "deleteContentForward") || e.isComposing || el.selectionStart !== el.selectionEnd) return;
      const caret = el.selectionStart;
      const cur = valueRef.current;
      const tag = cur.tags.find((t) => (back ? caret > t.start && caret <= t.end : caret >= t.start && caret < t.end));
      if (!tag) return;
      e.preventDefault();
      const insert = back ? tag.query : "";
      apply(replaceRange(cur, tag.start, tag.end, insert, tag), tag.start + insert.length);
    };
    el.addEventListener("beforeinput", onBefore);
    return () => el.removeEventListener("beforeinput", onBefore);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- 값은 ref로 읽는다

  /** 멘션 목록이 열려 있을 때의 키 (처리했으면 true) */
  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (isComposingKey(e) || !mention || !candidates.length) return false;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const d = e.key === "ArrowDown" ? 1 : -1;
      setMention({ ...mention, index: (mention.index + d + candidates.length) % candidates.length });
      return true;
    }
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      pick(candidates[mention.index]!);
      return true;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setMention(null);
      return true;
    }
    return false;
  };

  return {
    value,
    ref,
    backdrop,
    mention,
    candidates,
    pick,
    closeMention: () => setMention(null),
    onTextChange,
    onKeyDown,
    /** 글을 통째로 바꾼다 (보낸 뒤 비우기, 실패하면 되돌리기) */
    reset: (v: TaggedText) => {
      setMention(null);
      apply(v);
    },
    /** 보낼 글 ("@kernel") */
    body: () => toBody(valueRef.current),
  };
}

export type MentionFieldState = ReturnType<typeof useMentionField>;

/** 태그 자리에만 색을 칠하는 뒷판 (글자는 입력창이 그린다) */
function Backdrop({ value }: { value: TaggedText }): ReactNode {
  const out: ReactNode[] = [];
  let pos = 0;
  for (const t of value.tags) {
    if (t.start > pos) out.push(<Fragment key={`t${pos}`}>{value.text.slice(pos, t.start)}</Fragment>);
    out.push(
      <mark key={`m${t.start}`} className="mention-tag">
        {value.text.slice(t.start, t.end)}
      </mark>,
    );
    pos = t.end;
  }
  out.push(<Fragment key="end">{`${value.text.slice(pos)}​`}</Fragment>);
  return out;
}

export function MentionTextarea({ field, ...props }: { field: MentionFieldState } & Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange">) {
  return (
    <div className="mention-field">
      <div ref={field.backdrop} className="mention-backdrop" aria-hidden="true">
        <Backdrop value={field.value} />
      </div>
      <textarea
        {...props}
        ref={field.ref}
        value={field.value.text}
        onChange={field.onTextChange}
        onScroll={(e) => {
          if (field.backdrop.current) field.backdrop.current.scrollTop = e.currentTarget.scrollTop;
        }}
        onBlur={(e) => {
          field.closeMention();
          props.onBlur?.(e);
        }}
        aria-autocomplete="list"
        aria-expanded={!!field.mention && field.candidates.length > 0}
      />
    </div>
  );
}

export function MentionList({ field }: { field: MentionFieldState }) {
  const { mention, candidates } = field;
  if (!mention || candidates.length === 0) return null;
  return (
    <ul className="mention-list" role="listbox" aria-label="멘션할 사람">
      {candidates.map((u, i) => (
        <li
          key={u.id}
          role="option"
          aria-selected={i === mention.index}
          className={i === mention.index ? "active" : ""}
          onMouseDown={(e) => {
            e.preventDefault();
            field.pick(u);
          }}
        >
          <Avatar user={u} size={22} />
          <span className="name">{u.displayName}</span>
          <span className="handle">@{u.username}</span>
        </li>
      ))}
    </ul>
  );
}
